import { create } from 'zustand';
import { NETWORK_CONFIG } from '@/utils/constants';

/**
 * プレイヤー設定
 */
interface PlayerSettings {
  name: string;
  avatarId: string;
  stats: {
    gamesPlayed: number;
    gamesWon: number;
    winRate: number;
  };
}

/**
 * オーディオ設定
 */
interface AudioSettings {
  masterVolume: number;        // 0-100
  soundEffectsVolume: number;  // 0-100
  musicVolume: number;          // 0-100
  soundEnabled: boolean;
  musicEnabled: boolean;
}

/**
 * ビジュアル設定
 */
interface VisualSettings {
  animationSpeed: 'slow' | 'normal' | 'fast';
  animationsEnabled: boolean;
  cardTheme: 'classic' | 'modern' | 'retro';
  particleEffects: boolean;
}

/**
 * ゲームルール設定
 */
interface RulesSettings {
  aiDifficulty: 'easy' | 'medium' | 'hard';
  allowDrawStacking: boolean;
  allowJumpIn: boolean;
}

/**
 * ネットワーク設定
 */
interface NetworkSettings {
  preferredServerUrl: string;
  autoReconnect: boolean;
}

/**
 * 設定ストア状態
 */
export interface SettingsStoreState {
  player: PlayerSettings;
  audio: AudioSettings;
  visual: VisualSettings;
  rules: RulesSettings;
  network: NetworkSettings;

  // アクション
  updatePlayerName: (name: string) => void;
  updateAudio: (settings: Partial<AudioSettings>) => void;
  updateVisual: (settings: Partial<VisualSettings>) => void;
  updateRules: (settings: Partial<RulesSettings>) => void;
  updateNetwork: (settings: Partial<NetworkSettings>) => void;
  recordGamePlayed: (won: boolean) => void;
  resetStats: () => void;
  loadFromLocalStorage: () => void;
  saveToLocalStorage: () => void;
  resetToDefaults: () => void;
}

/**
 * デフォルト設定
 */
const DEFAULT_SETTINGS: Omit<SettingsStoreState, 'updatePlayerName' | 'updateAudio' | 'updateVisual' | 'updateRules' | 'updateNetwork' | 'recordGamePlayed' | 'resetStats' | 'loadFromLocalStorage' | 'saveToLocalStorage' | 'resetToDefaults'> = {
  player: {
    name: 'Player',
    avatarId: 'default',
    stats: {
      gamesPlayed: 0,
      gamesWon: 0,
      winRate: 0
    }
  },
  audio: {
    masterVolume: 80,
    soundEffectsVolume: 80,
    musicVolume: 60,
    // SFXファイルが揃ったため、商用標準の「効果音ON」をデフォルトとする
    soundEnabled: true,
    musicEnabled: true
  },
  visual: {
    animationSpeed: 'normal',
    animationsEnabled: true,
    cardTheme: 'classic',
    particleEffects: true
  },
  rules: {
    aiDifficulty: 'medium',
    allowDrawStacking: false,
    allowJumpIn: false
  },
  network: {
    preferredServerUrl: NETWORK_CONFIG.DEFAULT_SERVER_URL,
    autoReconnect: true
  }
};

/**
 * localStorage キー
 */
const STORAGE_KEY = 'uno-2v1-settings';

/**
 * 設定ストア
 * ユーザー設定の永続化と管理を行う
 */
export const useSettingsStore = create<SettingsStoreState>((set, get) => ({
  ...DEFAULT_SETTINGS,

  /**
   * プレイヤー名を更新
   * @param name 新しいプレイヤー名
   */
  updatePlayerName: (name: string) => {
    if (!name || name.trim().length === 0) {
      console.error('Player name cannot be empty');
      return;
    }

    set(state => ({
      player: {
        ...state.player,
        name: name.trim()
      }
    }));
    get().saveToLocalStorage();
  },

  /**
   * オーディオ設定を更新
   * @param settings オーディオ設定の部分更新
   */
  updateAudio: (settings: Partial<AudioSettings>) => {
    // ボリューム値のバリデーション
    if (settings.masterVolume !== undefined && (settings.masterVolume < 0 || settings.masterVolume > 100)) {
      console.error('Volume must be between 0 and 100');
      return;
    }
    if (settings.soundEffectsVolume !== undefined && (settings.soundEffectsVolume < 0 || settings.soundEffectsVolume > 100)) {
      console.error('Volume must be between 0 and 100');
      return;
    }
    if (settings.musicVolume !== undefined && (settings.musicVolume < 0 || settings.musicVolume > 100)) {
      console.error('Volume must be between 0 and 100');
      return;
    }

    set(state => ({
      audio: { ...state.audio, ...settings }
    }));
    get().saveToLocalStorage();
  },

  /**
   * ビジュアル設定を更新
   * @param settings ビジュアル設定の部分更新
   */
  updateVisual: (settings: Partial<VisualSettings>) => {
    // アニメーション速度のバリデーション
    if (settings.animationSpeed) {
      const validSpeeds: Array<'slow' | 'normal' | 'fast'> = ['slow', 'normal', 'fast'];
      if (!validSpeeds.includes(settings.animationSpeed)) {
        console.error('Invalid animation speed');
        return;
      }
    }

    // カードテーマのバリデーション
    if (settings.cardTheme) {
      const validThemes: Array<'classic' | 'modern' | 'retro'> = ['classic', 'modern', 'retro'];
      if (!validThemes.includes(settings.cardTheme)) {
        console.error('Invalid card theme');
        return;
      }
    }

    set(state => ({
      visual: { ...state.visual, ...settings }
    }));
    get().saveToLocalStorage();
  },

  /**
   * ゲームルール設定を更新
   * @param settings ルール設定の部分更新
   */
  updateRules: (settings: Partial<RulesSettings>) => {
    // AI難易度のバリデーション
    if (settings.aiDifficulty) {
      const validDifficulties: Array<'easy' | 'medium' | 'hard'> = ['easy', 'medium', 'hard'];
      if (!validDifficulties.includes(settings.aiDifficulty)) {
        console.error('Invalid AI difficulty');
        return;
      }
    }

    set(state => ({
      rules: { ...state.rules, ...settings }
    }));
    get().saveToLocalStorage();
  },

  /**
   * ネットワーク設定を更新
   * @param settings ネットワーク設定の部分更新
   */
  updateNetwork: (settings: Partial<NetworkSettings>) => {
    // サーバーURLのバリデーション
    if (settings.preferredServerUrl !== undefined) {
      if (!settings.preferredServerUrl || settings.preferredServerUrl.trim().length === 0) {
        console.error('Server URL cannot be empty');
        return;
      }
    }

    set(state => ({
      network: { ...state.network, ...settings }
    }));
    get().saveToLocalStorage();
  },

  /**
   * ゲーム結果を記録
   * @param won 勝利したかどうか
   */
  recordGamePlayed: (won: boolean) => {
    set(state => {
      const newGamesPlayed = state.player.stats.gamesPlayed + 1;
      const newGamesWon = won ? state.player.stats.gamesWon + 1 : state.player.stats.gamesWon;
      const newWinRate = (newGamesWon / newGamesPlayed) * 100;

      return {
        player: {
          ...state.player,
          stats: {
            gamesPlayed: newGamesPlayed,
            gamesWon: newGamesWon,
            winRate: Math.round(newWinRate * 100) / 100 // 小数点2桁まで
          }
        }
      };
    });

    get().saveToLocalStorage();
  },

  /**
   * 統計をリセット
   */
  resetStats: () => {
    set(state => ({
      player: {
        ...state.player,
        stats: {
          gamesPlayed: 0,
          gamesWon: 0,
          winRate: 0
        }
      }
    }));

    get().saveToLocalStorage();
  },

  /**
   * localStorageから設定を読み込み
   */
  loadFromLocalStorage: () => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);

        // デフォルト設定とマージ（新しいフィールドの追加に対応）
        set({
          ...DEFAULT_SETTINGS,
          ...parsed,
          player: { ...DEFAULT_SETTINGS.player, ...parsed.player },
          audio: { ...DEFAULT_SETTINGS.audio, ...parsed.audio },
          visual: { ...DEFAULT_SETTINGS.visual, ...parsed.visual },
          rules: { ...DEFAULT_SETTINGS.rules, ...parsed.rules },
          network: { ...DEFAULT_SETTINGS.network, ...parsed.network }
        });

        console.log('Settings loaded from localStorage');
      }
    } catch (error) {
      console.error('Failed to load settings from localStorage:', error);
      // エラー時はデフォルト設定を使用
      set(DEFAULT_SETTINGS);
    }
  },

  /**
   * 設定をlocalStorageに保存
   */
  saveToLocalStorage: () => {
    try {
      const state = get();

      // アクションメソッドを除外してシリアライズ
      const dataToSave = {
        player: state.player,
        audio: state.audio,
        visual: state.visual,
        rules: state.rules,
        network: state.network
      };

      localStorage.setItem(STORAGE_KEY, JSON.stringify(dataToSave));
      console.log('Settings saved to localStorage');
    } catch (error) {
      console.error('Failed to save settings to localStorage:', error);

      console.warn('Could not save settings - localStorage may be full or disabled');
    }
  },

  /**
   * 設定をデフォルトにリセット
   */
  resetToDefaults: () => {
    set(DEFAULT_SETTINGS);
    get().saveToLocalStorage();
    console.log('Settings reset to defaults');
  }
}));

// 型エクスポート
export type {
  PlayerSettings,
  AudioSettings,
  VisualSettings,
  RulesSettings,
  NetworkSettings
};
